<template>
  <div class="quick-apply">
    <!-- Backdrop: contexto interno (acima do perfil) -->
    <div class="quick-apply__backdrop">
      <img
        v-if="company.logo_url"
        :src="company.logo_url"
        :alt="company.name"
        class="quick-apply__backdrop-logo"
      />
      <h2 v-if="company.name" class="quick-apply__backdrop-title">
        {{ company.name }}
      </h2>
      <p class="quick-apply__backdrop-text">Estamos preparando sua candidatura...</p>
    </div>

    <!-- Modal -->
    <div class="quick-apply__overlay">
      <div class="quick-apply__modal">
        <!-- LOADING inicial -->
        <div v-if="state === 'loading'" class="quick-apply__center">
          <div class="qa-spinner" :style="{ borderTopColor: color }"></div>
          <p class="quick-apply__muted">Carregando dados da vaga...</p>
        </div>

        <!-- CONFIRMACAO -->
        <template v-else-if="state === 'confirm'">
          <div class="quick-apply__header">
            <img
              v-if="company.logo_url"
              :src="company.logo_url"
              :alt="company.name"
              class="quick-apply__logo"
            />
            <span class="quick-apply__company">{{ company.name }}</span>
          </div>

          <h1 class="quick-apply__title">{{ title }}</h1>

          <div v-if="locationLabel" class="quick-apply__location">
            <feather-icon
              :icon="vacancy.remote === 1 ? 'WifiIcon' : 'MapPinIcon'"
              svgClasses="h-4 w-4"
            />
            <span>{{ locationLabel }}</span>
          </div>

          <p class="quick-apply__question">
            Deseja confirmar sua candidatura para esta vaga?
          </p>

          <p v-if="hasForms" class="quick-apply__hint">
            Esta vaga possui perguntas adicionais. Você as responderá logo após confirmar.
          </p>

          <div class="quick-apply__actions">
            <button class="qa-btn qa-btn--ghost" @click="cancel">Cancelar</button>
            <button
              class="qa-btn qa-btn--primary"
              :style="{ backgroundColor: color }"
              @click="confirmApplication"
            >
              Confirmar candidatura
            </button>
          </div>
        </template>

        <!-- ENVIANDO -->
        <div v-else-if="state === 'submitting'" class="quick-apply__center">
          <div class="qa-spinner" :style="{ borderTopColor: color }"></div>
          <p class="quick-apply__muted">Enviando sua candidatura...</p>
        </div>

        <!-- SUCESSO -->
        <div v-else-if="state === 'success'" class="quick-apply__center">
          <div class="qa-checkmark" :style="{ borderColor: color }">
            <svg class="qa-checkmark__svg" viewBox="0 0 52 52">
              <path
                class="qa-checkmark__check"
                :style="{ stroke: color }"
                fill="none"
                d="M14 27l8 8 16-16"
              />
            </svg>
          </div>
          <h2 class="quick-apply__success-title">Candidatura confirmada!</h2>
          <p class="quick-apply__muted">
            {{
              hasForms
                ? "Vamos às perguntas da vaga..."
                : "Boa sorte no processo seletivo."
            }}
          </p>
          <button
            v-if="hasForms"
            class="qa-btn qa-btn--primary mt-4"
            :style="{ backgroundColor: color }"
            @click="goToQuestions"
          >
            Responder perguntas
          </button>
        </div>

        <!-- JA CANDIDATADO -->
        <div v-else-if="state === 'already-applied'" class="quick-apply__center">
          <feather-icon icon="CheckCircleIcon" svgClasses="h-12 w-12" :style="{ color: color }" />
          <h2 class="quick-apply__success-title">Você já se candidatou</h2>

          <template v-if="hasForms && !alreadyAnswered">
            <p class="quick-apply__muted">
              Sua candidatura para <b>{{ title }}</b> já foi registrada, mas ainda
              faltam algumas perguntas da vaga para completar.
            </p>
            <button
              class="qa-btn qa-btn--primary mt-4"
              :style="{ backgroundColor: color }"
              @click="goToQuestions"
            >
              Responder perguntas
            </button>
          </template>

          <template v-else>
            <p class="quick-apply__muted">
              Sua candidatura para <b>{{ title }}</b> já foi registrada.
            </p>
            <button
              class="qa-btn qa-btn--primary mt-4"
              :style="{ backgroundColor: color }"
              @click="goToProfile"
            >
              Ok, voltar ao meu perfil
            </button>
          </template>
        </div>

        <!-- ERRO / NAO ENCONTRADA -->
        <div v-else class="quick-apply__center">
          <feather-icon icon="AlertCircleIcon" svgClasses="h-12 w-12 text-warning" />
          <h2 class="quick-apply__success-title">{{ errorTitle }}</h2>
          <p class="quick-apply__muted">{{ errorMessage }}</p>
          <div class="quick-apply__actions mt-4">
            <button class="qa-btn qa-btn--ghost" @click="cancel">Ir para minhas vagas</button>
            <button
              v-if="state === 'error'"
              class="qa-btn qa-btn--primary"
              :style="{ backgroundColor: color }"
              @click="confirmApplication"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchPublicVacancy,
  checkVacancyForms,
  checkFormsAnswered,
  createApplication,
  fetchExistingApplication,
} from "@/services/application";
import { trackNewApplication, trackEvent } from "@/services/posthog";

export default {
  name: "quick-apply",
  data: () => ({
    state: "loading", // loading | confirm | submitting | success | already-applied | error | notfound
    company: { id: null, name: "", logo_url: "", base_hexcolor: "" },
    vacancy: {},
    hasForms: false,
    stepId: null,
    alreadyAnswered: true,
    errorTitle: "",
    errorMessage: "",
  }),
  computed: {
    color() {
      return (
        this.company.base_hexcolor ||
        this.$store.getters["company/color"] ||
        "#1551b1"
      );
    },
    companyLabel() {
      return this.$router.history.current.params.company_label;
    },
    routeVacancyId() {
      return this.$router.history.current.params.vacancy_id;
    },
    title() {
      // O endpoint /v2/companies retorna o titulo da vaga em `vacancy.name`.
      return this.vacancy && this.vacancy.name ? this.vacancy.name : "";
    },
    // Modelo de trabalho + local, ja com validacao de compartilhamento feita no
    // backend (location vem null quando a vaga nao divulga o local).
    locationLabel() {
      const v = this.vacancy || {};
      const model = v.work_model || "";
      const loc = v.location || "";
      if (model && loc) return `${model} · ${loc}`;
      return model || loc || "";
    },
  },
  methods: {
    async load() {
      try {
        const { company, vacancy } = await fetchPublicVacancy(
          this.companyLabel,
          this.routeVacancyId
        );

        if (!vacancy || !vacancy.id) {
          this.setError(
            "notfound",
            "Vaga não encontrada",
            "Esta vaga não está mais disponível. Confira as demais oportunidades."
          );
          return;
        }

        this.company = company;
        this.vacancy = vacancy;

        // Perguntas personalizadas + ja respondidas (em paralelo com a checagem
        // de candidatura existente, que tambem valida o token via interceptor).
        const [forms, existing] = await Promise.all([
          checkVacancyForms(vacancy.id),
          fetchExistingApplication(vacancy.id),
        ]);

        this.hasForms = forms.hasForms;
        this.stepId = forms.stepId;

        if (this.hasForms) {
          this.alreadyAnswered = await checkFormsAnswered(
            vacancy.id,
            this.stepId
          );
        }

        if (existing.exists) {
          this.state = "already-applied";
          trackEvent("quick_apply_already_applied", {
            vacancy_id: vacancy.id,
            company_label: this.companyLabel,
          });
          return;
        }

        this.state = "confirm";
        trackEvent("quick_apply_shown", {
          vacancy_id: vacancy.id,
          company_label: this.companyLabel,
          has_forms: this.hasForms,
        });
      } catch (error) {
        // 401 ja foi tratado pelo interceptor (redireciona para signin).
        this.setError(
          "notfound",
          "Vaga não encontrada",
          "Não foi possível carregar esta vaga. Confira as demais oportunidades."
        );
      }
    },
    async confirmApplication() {
      this.state = "submitting";
      try {
        const { status, created } = await createApplication({
          companyLabel: this.companyLabel,
          vacancyId: this.vacancy.id,
          companyId: this.company.id,
          managerId: this.vacancy.manager_id,
        });

        if (status === 200 && created) {
          trackNewApplication({
            vacancy_id: this.vacancy.id,
            vacancy_title: this.title,
            company_id: this.company.id,
            company_name: this.company.name,
            company_label: this.companyLabel,
          });
          trackEvent("quick_apply_confirmed", {
            vacancy_id: this.vacancy.id,
            company_label: this.companyLabel,
            has_forms: this.hasForms,
          });

          try {
            if (window.woopra) {
              window.woopra.track("company-vacancy-apply-click", {
                vacancy_id: this.vacancy.id,
                company_id: this.company.id,
                manager_id: this.vacancy.manager_id,
              });
            }
          } catch (_) {
            // no-op: woopra é best-effort
          }

          this.state = "success";

          if (this.hasForms) {
            this.redirectTimer = setTimeout(this.goToQuestions, 1600);
          } else {
            this.redirectTimer = setTimeout(this.goToDashboard, 1600);
          }
        } else {
          this.setError(
            "error",
            "Não foi possível candidatar",
            "Tente novamente em instantes."
          );
        }
      } catch (error) {
        // Candidatura duplicada: o backend (ApplicationsController@store) responde
        // 400 com a mensagem "Você já se candidatou...". Tratamos como ja-candidatado
        // e, em seguida, a propria modal verifica perguntas pendentes.
        const status = error && error.response && error.response.status;
        const message = (
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.message) ||
          ""
        ).toLowerCase();
        const isDuplicate =
          status === 409 ||
          (status === 400 && message.includes("já se candidat"));

        if (isDuplicate) {
          this.state = "already-applied";
          return;
        }
        this.setError(
          "error",
          "Não foi possível candidatar",
          "Ocorreu um erro ao registrar sua candidatura. Tente novamente."
        );
      }
    },
    goToQuestions() {
      this.clearTimer();
      this.$router.push({
        name: "application-process",
        params: {
          company_label: this.companyLabel,
          vacancy_id: this.vacancy.id,
          step_id: this.stepId,
        },
      });
    },
    goToDashboard() {
      this.clearTimer();
      this.$router.push({
        name: "dashboard-vacancy",
        params: {
          company_label: this.companyLabel,
          vacancy_id: this.vacancy.id || this.routeVacancyId,
        },
        query: { success: true },
      });
    },
    // Sair sem candidatar (cancelar/erro): vai para as vagas SEM o success=true,
    // que e o que dispara a mensagem "candidatura confirmada" na tela /vacancy.
    cancel() {
      this.clearTimer();
      this.$router.push({
        name: "dashboard-vacancy",
        params: {
          company_label: this.companyLabel,
          vacancy_id: this.vacancy.id || this.routeVacancyId,
        },
      });
    },
    goToProfile() {
      this.clearTimer();
      this.$router.push({
        name: "dashboard-profile-detail",
        params: {
          company_label: this.companyLabel,
          vacancy_id: this.vacancy.id || this.routeVacancyId,
        },
      });
    },
    setError(state, title, message) {
      this.state = state;
      this.errorTitle = title;
      this.errorMessage = message;
    },
    clearTimer() {
      if (this.redirectTimer) {
        clearTimeout(this.redirectTimer);
        this.redirectTimer = null;
      }
    },
  },
  created() {
    window.scrollTo(0, 0);
    this.load();
  },
  beforeDestroy() {
    this.clearTimer();
  },
};
</script>

<style lang="scss" scoped>
.quick-apply {
  position: relative;
  width: 100%;
  min-height: 70vh;
}

.quick-apply__backdrop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 16px;
  filter: blur(1px);
  opacity: 0.6;
}

.quick-apply__backdrop-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 12px;
  margin-bottom: 12px;
}

.quick-apply__backdrop-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.quick-apply__backdrop-text {
  color: #696969;
  margin-top: 4px;
}

.quick-apply__overlay {
  position: fixed;
  inset: 0;
  background: rgba(20, 23, 31, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 52000;
}

.quick-apply__modal {
  width: 100%;
  max-width: 440px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: qa-pop 0.25s ease;
}

@keyframes qa-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.quick-apply__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.quick-apply__logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 8px;
}

.quick-apply__company {
  color: #4f4f4f;
  font-size: 14px;
  font-weight: 500;
}

.quick-apply__title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.25;
  margin-bottom: 8px;
}

.quick-apply__location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #696969;
  font-size: 14px;
  margin-bottom: 16px;
}

.quick-apply__question {
  color: #333;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 8px;
}

.quick-apply__hint {
  color: #696969;
  font-size: 13px;
  margin-bottom: 8px;
}

.quick-apply__actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

/* Em linha (confirmar/cancelar), os botoes dividem a largura igualmente. */
.quick-apply__actions .qa-btn {
  flex: 1 1 0;
  width: auto;
}

.quick-apply__center {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 4px;
}

.quick-apply__success-title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 16px;
}

.quick-apply__muted {
  color: #696969;
  font-size: 14px;
  margin-top: 8px;
}

.qa-btn {
  flex: 0 0 auto;
  width: 100%;
  height: 46px;
  min-height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease;
  border: none;
  box-sizing: border-box;
}

.qa-btn:hover {
  filter: brightness(0.95);
}

.qa-btn--primary {
  color: #fff;
  background: #1551b1;
}

.qa-btn--ghost {
  background: #fff;
  color: #4f4f4f;
  border: 0.5px solid #a8a8a8;
}

.mt-4 {
  margin-top: 16px;
}

/* Spinner */
.qa-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e6e6e6;
  border-top-color: #1551b1;
  border-radius: 50%;
  animation: qa-spin 0.8s linear infinite;
}

@keyframes qa-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Checkmark */
.qa-checkmark {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid #1551b1;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: qa-pop 0.3s ease;
}

.qa-checkmark__svg {
  width: 48px;
  height: 48px;
}

.qa-checkmark__check {
  stroke: #1551b1;
  stroke-width: 4;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: qa-draw 0.5s 0.15s ease forwards;
}

@keyframes qa-draw {
  to {
    stroke-dashoffset: 0;
  }
}
</style>
